import type {
  HealthGrade,
  HealthScoreInput,
  HealthScoreResult,
  VisibilityIndexResult,
} from "./types";
import {
  COVERAGE_WEIGHT,
  GRADE_METADATA,
  GRADE_THRESHOLDS,
  MOMENTUM_WEIGHT,
  NOT_RANKED_PENALTY_POSITION,
  POSITION_SCALE_MAX,
  POSITION_WEIGHT,
  STABILITY_BAND,
} from "./constants";

export function computeCoverageScore(
  totalPairs: number,
  rankedPairs: number,
): number {
  if (totalPairs === 0) return 0;
  return (rankedPairs / totalPairs) * 100;
}

export function computePositionScore(
  totalPairs: number,
  rankedPairs: number,
  rankedPositionSum: number,
): number {
  if (totalPairs === 0) return 0;

  const unrankedPairs = totalPairs - rankedPairs;
  const effectiveSum =
    rankedPositionSum + unrankedPairs * NOT_RANKED_PENALTY_POSITION;
  const effectiveAvg = effectiveSum / totalPairs;

  return Math.max(
    0,
    Math.round(100 * (1 - (effectiveAvg - 1) / POSITION_SCALE_MAX)),
  );
}

/**
 * Computes momentum from two time-window averages.
 * Positive delta = positions improved (lower number is better in rankings).
 * A stability band of +/-2 dampens noise from normal OTA volatility.
 */
export function computeMomentumScore(
  recentAvg: number | null,
  olderAvg: number | null,
): number {
  if (recentAvg === null || olderAvg === null) return 50;

  const delta = olderAvg - recentAvg; // positive = improved

  if (Math.abs(delta) <= STABILITY_BAND) {
    return 55;
  }

  if (delta >= 5) return 95;
  if (delta >= 3) return 80;

  if (delta <= -5) return 10;
  if (delta <= -3) return 35;

  // Marginal movement just outside the stability band (2 < |delta| < 3)
  return delta > 0 ? 65 : 45;
}

function resolveGrade(composite: number): HealthGrade {
  for (const { grade, min } of GRADE_THRESHOLDS) {
    if (composite >= min) return grade;
  }
  return "F";
}

function resolveVisibilityLabel(percentage: number): string {
  if (percentage >= 80) return "Excellent visibility";
  if (percentage >= 60) return "Good visibility";
  if (percentage >= 40) return "Fair visibility";
  return "Poor visibility";
}

export function calculateHealthScore(
  input: HealthScoreInput,
): HealthScoreResult {
  if (!input.hasCompletedScrape && input.rankedPairs === 0) {
    return { status: "gathering_data" };
  }

  const coverage = computeCoverageScore(input.totalPairs, input.rankedPairs);
  const position = computePositionScore(
    input.totalPairs,
    input.rankedPairs,
    input.rankedPositionSum,
  );
  const momentum = computeMomentumScore(
    input.recentAvgPosition,
    input.olderAvgPosition,
  );

  const composite = Math.round(
    coverage * COVERAGE_WEIGHT +
      position * POSITION_WEIGHT +
      momentum * MOMENTUM_WEIGHT,
  );

  const grade = resolveGrade(composite);
  const meta = GRADE_METADATA[grade];

  return {
    status: "scored",
    grade,
    label: meta.label,
    color: meta.color,
    composite,
  };
}

export function calculateVisibilityIndex(
  top20Count: number,
  totalPairs: number,
): VisibilityIndexResult {
  if (totalPairs === 0) {
    return { percentage: 0, label: "No data", top20Count: 0, totalPairs: 0 };
  }

  const percentage = Math.min(
    100,
    Math.round((top20Count / totalPairs) * 100),
  );

  return { percentage, label: resolveVisibilityLabel(percentage), top20Count, totalPairs };
}
